/**
 * Authentication Components for HoloLab Teacher-Student Platform
 * Uses Clerk for authentication with role-based access control
 */
/* eslint-disable react/no-unknown-property */

import { ConvexProviderWithClerk } from "convex/react-clerk";
import { ClerkProvider, useAuth, SignIn, SignUp, useUser } from "@clerk/clerk-react";
import { ConvexReactClient, useMutation } from "convex/react";
import { dark } from "@clerk/themes";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useThemeStore } from "./guided/titration/store/themeStore";
import ThemeToggle from "./guided/titration/panels/ThemeToggle";
import React from "react";
import { useNavigate } from "react-router-dom";
import { GraduationCap, Briefcase, Repeat, Loader2 } from "lucide-react";
import { api } from "../convex/_generated/api";

const convex = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL as string);

interface AuthWrapperProps {
  children: React.ReactNode;
}

// ─── THEME-AWARE 3D BACKGROUND ─────────────────────────────────────────────
interface ThemeProps { isDark: boolean }

const FloatingShapes: React.FC<ThemeProps> = ({ isDark }) => {
  const r1 = React.useRef<THREE.Mesh>(null);
  const r2 = React.useRef<THREE.Mesh>(null);
  const r3 = React.useRef<THREE.Mesh>(null);
  const r4 = React.useRef<THREE.Mesh>(null);

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (r1.current) {
      r1.current.rotation.x = t * 0.2;
      r1.current.rotation.y = t * 0.3;
      r1.current.position.y = Math.sin(t * 0.5) * 0.2;
    }
    if (r2.current) {
      r2.current.rotation.x = t * 0.15;
      r2.current.rotation.z = t * 0.25;
      r2.current.position.y = Math.sin(t * 0.4 + 1) * 0.15;
    }
    if (r3.current) {
      r3.current.rotation.y = t * 0.2;
      r3.current.rotation.z = t * 0.1;
      r3.current.position.y = Math.sin(t * 0.6 + 2) * 0.1;
    }
    if (r4.current) {
      r4.current.rotation.x = t * 0.12;
      r4.current.rotation.y = t * 0.18;
      r4.current.position.y = Math.sin(t * 0.35 + 3) * 0.18;
    }
  });

  const c1 = isDark ? "#8b5cf6" : "#2563eb";  // violet / blue
  const c2 = isDark ? "#06b6d4" : "#0d9488";  // cyan / teal
  const c3 = isDark ? "#d946ef" : "#7c3aed";  // magenta / purple
  const c4 = isDark ? "#a78bfa" : "#3b82f6";  // light-violet / sky-blue
  const op = isDark ? 0.35 : 0.22;

  return (
    <>
      <mesh ref={r1} position={[-3.5, 2, -2]}>
        <icosahedronGeometry args={[0.65, 0]} />
        <meshBasicMaterial color={c1} wireframe transparent opacity={op} />
      </mesh>
      <mesh ref={r2} position={[3.8, -1.8, -3]}>
        <octahedronGeometry args={[0.55, 0]} />
        <meshBasicMaterial color={c2} wireframe transparent opacity={op * 0.85} />
      </mesh>
      <mesh ref={r3} position={[2.8, 2.8, -4]}>
        <torusGeometry args={[0.45, 0.16, 8, 20]} />
        <meshBasicMaterial color={c3} wireframe transparent opacity={op * 0.6} />
      </mesh>
      <mesh ref={r4} position={[-4, -2.5, -3.5]}>
        <dodecahedronGeometry args={[0.5, 0]} />
        <meshBasicMaterial color={c4} wireframe transparent opacity={op * 0.5} />
      </mesh>
    </>
  );
};

const AmbientParticles: React.FC<ThemeProps> = ({ isDark }) => {
  const ref1 = React.useRef<THREE.Points>(null);
  const ref2 = React.useRef<THREE.Points>(null);
  const count = 90;

  const { p1, v1, p2, v2 } = React.useMemo(() => {
    const p1 = new Float32Array(count * 3);
    const v1 = new Float32Array(count * 3);
    const p2 = new Float32Array(count * 3);
    const v2 = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      p1[i*3]   = (Math.random()-0.5)*12;
      p1[i*3+1] = (Math.random()-0.5)*8;
      p1[i*3+2] = (Math.random()-0.5)*6-3;
      v1[i*3]   = (Math.random()-0.5)*0.0018;
      v1[i*3+1] = (Math.random()-0.5)*0.0018;
      p2[i*3]   = (Math.random()-0.5)*12;
      p2[i*3+1] = (Math.random()-0.5)*8;
      p2[i*3+2] = (Math.random()-0.5)*6-3;
      v2[i*3]   = (Math.random()-0.5)*0.0022;
      v2[i*3+1] = (Math.random()-0.5)*0.0022;
    }
    return { p1, v1, p2, v2 };
  }, []);

  useFrame(() => {
    const drift = (arr: Float32Array, vel: Float32Array) => {
      for (let i = 0; i < count; i++) {
        arr[i*3]   += vel[i*3];
        arr[i*3+1] += vel[i*3+1];
        if (arr[i*3]   >  6) arr[i*3]   = -6;
        if (arr[i*3]   < -6) arr[i*3]   =  6;
        if (arr[i*3+1] >  4) arr[i*3+1] = -4;
        if (arr[i*3+1] < -4) arr[i*3+1] =  4;
      }
    };
    if (ref1.current) {
      drift(ref1.current.geometry.attributes.position.array as Float32Array, v1);
      ref1.current.geometry.attributes.position.needsUpdate = true;
    }
    if (ref2.current) {
      drift(ref2.current.geometry.attributes.position.array as Float32Array, v2);
      ref2.current.geometry.attributes.position.needsUpdate = true;
    }
  });

  const col1 = isDark ? "#8b5cf6" : "#2563eb";
  const col2 = isDark ? "#06b6d4" : "#0d9488";
  const op1  = isDark ? 0.70 : 0.45;
  const op2  = isDark ? 0.55 : 0.38;

  return (
    <>
      <points ref={ref1}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={count} array={p1} itemSize={3} args={[p1, 3]} />
        </bufferGeometry>
        <pointsMaterial size={0.035} color={col1} transparent opacity={op1} sizeAttenuation />
      </points>
      <points ref={ref2}>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" count={count} array={p2} itemSize={3} args={[p2, 3]} />
        </bufferGeometry>
        <pointsMaterial size={0.028} color={col2} transparent opacity={op2} sizeAttenuation />
      </points>
    </>
  );
};

export function AuthWrapper({ children }: AuthWrapperProps) {
  const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  const isDark = useThemeStore((state) => state.resolvedTheme === 'dark');

  if (!publishableKey) {
    console.error("Missing Clerk Publishable Key. Add VITE_CLERK_PUBLISHABLE_KEY to .env");
    return <>{children}</>;
  }
  return (
    <ClerkProvider 
      publishableKey={publishableKey} 
      appearance={{ baseTheme: isDark ? dark : undefined, variables: { colorPrimary: "#4f46e5" } }}
    >
      <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
        {children}
      </ConvexProviderWithClerk>
    </ClerkProvider>
  );
}

// ── Shared Auth Background ─────────────────────────────────────────────────
function AuthBackground({ children }: { children: React.ReactNode }) {
  const isDark = useThemeStore((state) => state.resolvedTheme === 'dark');

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#030712] relative overflow-hidden px-4 py-8 transition-colors duration-500">
      <div className="absolute top-4 right-4 z-50">
        <ThemeToggle />
      </div>

      {/* 3D Interactive Background */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <Canvas camera={{ position: [0, 0, 5], fov: 60 }} gl={{ alpha: true }}>
          <ambientLight intensity={isDark ? 0.3 : 0.8} />
          <pointLight position={[10, 10, 10]} intensity={isDark ? 0.8 : 1.5} color={isDark ? "#8b5cf6" : "#6366f1"} />
          <FloatingShapes isDark={isDark} />
          <AmbientParticles isDark={isDark} />
        </Canvas>
      </div>

      {/* Aurora blobs */}
      <div className="absolute z-0 -top-1/3 -left-1/4 w-[60vw] h-[60vw] rounded-full animate-aurora pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.04) 0%, transparent 65%)' }} />
      <div className="absolute z-0 -bottom-1/3 -right-1/4 w-[50vw] h-[50vw] rounded-full animate-aurora2 pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.03) 0%, transparent 65%)' }} />
      
      {/* Grid */}
      <div className="absolute z-0 inset-0 opacity-[0.03] dark:opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(139,92,246,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.8) 1px, transparent 1px)`,
          backgroundSize: '56px 56px',
        }} />
      <div className="relative z-10 w-full">{children}</div>
    </div>
  );
}

// ── Brand Logo Mark ────────────────────────────────────────────────────────
function BrandMark({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const sz = size === 'lg' ? 'w-14 h-14' : size === 'sm' ? 'w-8 h-8' : 'w-11 h-11';
  return (
    <img src="/logo_only.png" alt="HoloLab Logo" className={`${sz} object-contain`} />
  );
}

// ── Sign In Page ───────────────────────────────────────────────────────────
export function SignInPage() {
  const isDark = useThemeStore((state) => state.resolvedTheme === 'dark');

  return (
    <AuthBackground>
      <div className="flex flex-col items-center gap-6 w-full max-w-md mx-auto">
        <div className="flex items-center gap-3">
          <BrandMark size="md" />
          <span className="text-xl font-bold text-slate-900 dark:text-white font-space">HoloLab</span>
        </div>
        <SignIn
          appearance={{
            baseTheme: isDark ? dark : undefined,
            elements: {
              rootBox: "w-full",
              card: "shadow-2xl rounded-2xl w-full bg-slate-50 dark:bg-[#0d0d1a] border border-slate-200 dark:border-violet-500/15",
              headerTitle: "text-slate-900 dark:text-white font-space",
              headerSubtitle: "text-slate-500 dark:text-slate-400",
              socialButtonsBlockButton: "border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-700 dark:text-white hover:bg-slate-50 dark:hover:bg-white/10",
              dividerLine: "bg-slate-200 dark:bg-white/10",
              dividerText: "text-slate-500",
              formFieldLabel: "text-slate-700 dark:text-slate-300",
              formFieldInput: "bg-white dark:bg-[#12121f] border-slate-300 dark:border-violet-500/20 text-slate-900 dark:text-white focus:border-violet-500/50",
              formButtonPrimary: "bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400",
              footerActionLink: "text-violet-600 dark:text-violet-400 hover:text-violet-500 dark:hover:text-violet-300",
              identityPreviewText: "text-slate-700 dark:text-slate-300",
              identityPreviewEditButton: "text-violet-600 dark:text-violet-400",
            }
          }}
          signUpUrl="/sign-up"
          fallbackRedirectUrl="/select-role"
        />
      </div>
    </AuthBackground>
  );
}

// ── Sign Up Page ───────────────────────────────────────────────────────────
export function SignUpPage() {
  const isDark = useThemeStore((state) => state.resolvedTheme === 'dark');

  return (
    <AuthBackground>
      <div className="flex flex-col items-center gap-6 w-full max-w-md mx-auto">
        <div className="flex items-center gap-3">
          <BrandMark size="md" />
          <span className="text-xl font-bold text-slate-900 dark:text-white font-space">HoloLab</span>
        </div>
        <SignUp
          appearance={{
            baseTheme: isDark ? dark : undefined,
            elements: {
              rootBox: "w-full",
              card: "shadow-2xl rounded-2xl w-full bg-slate-50 dark:bg-[#0d0d1a] border border-slate-200 dark:border-violet-500/15",
              headerTitle: "text-slate-900 dark:text-white font-space",
              headerSubtitle: "text-slate-500 dark:text-slate-400",
              socialButtonsBlockButton: "border border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 text-slate-700 dark:text-white hover:bg-slate-50 dark:hover:bg-white/10",
              dividerLine: "bg-slate-200 dark:bg-white/10",
              dividerText: "text-slate-500",
              formFieldLabel: "text-slate-700 dark:text-slate-300",
              formFieldInput: "bg-white dark:bg-[#12121f] border-slate-300 dark:border-violet-500/20 text-slate-900 dark:text-white focus:border-violet-500/50",
              formButtonPrimary: "bg-gradient-to-r from-violet-600 to-cyan-500 hover:from-violet-500 hover:to-cyan-400",
              footerActionLink: "text-violet-600 dark:text-violet-400 hover:text-violet-500 dark:hover:text-violet-300",
            }
          }}
          signInUrl="/sign-in"
          fallbackRedirectUrl="/select-role"
        />
      </div>
    </AuthBackground>
  );
}

// ── Role Selection Page ────────────────────────────────────────────────────
export function RoleSelectionPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [isUpdating, setIsUpdating] = React.useState<'teacher' | 'student' | null>(null);

  React.useEffect(() => {
    const existingRole = user?.unsafeMetadata?.role as string;
    if (existingRole === 'teacher') navigate('/teacher/dashboard');
    else if (existingRole === 'student') navigate('/student/dashboard');
  }, [user, navigate]);

  const updateRole = async (role: 'teacher' | 'student') => {
    if (!user) return;
    setIsUpdating(role);
    try {
      await user.update({ unsafeMetadata: { role } });
      navigate(role === 'teacher' ? '/teacher/dashboard' : '/student/dashboard');
    } catch (error) {
      console.error('Failed to update role:', error);
      setIsUpdating(null);
    }
  };

  if (!user) {
    return (
      <AuthBackground>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="w-10 h-10 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
        </div>
      </AuthBackground>
    );
  }

  return (
    <AuthBackground>
      <div className="max-w-3xl mx-auto w-full">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <BrandMark size="lg" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white font-space mb-3">
            Choose Your Journey
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg max-w-sm mx-auto">
            Select your role to personalise your HoloLab experience.
          </p>
        </div>

        {/* Role cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

          {/* Teacher card */}
          <button
            onClick={() => updateRole('teacher')}
            disabled={isUpdating !== null}
            className="group relative p-7 rounded-2xl text-left bg-white/50 dark:bg-transparent backdrop-blur-sm transition-all duration-300 disabled:opacity-60 hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 shadow-xl dark:shadow-none"
            style={{
              background: 'linear-gradient(145deg, rgba(37,99,235,0.08) 0%, rgba(13,148,136,0.06) 100%)',
              border: '1px solid rgba(37,99,235,0.2)',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(37,99,235,0.5)';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 40px rgba(37,99,235,0.12)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(37,99,235,0.2)';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
            }}
          >
            {/* Icon */}
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
              style={{ background: 'linear-gradient(135deg, #1d4ed8 0%, #0d9488 100%)' }}>
              <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
              </svg>
            </div>

            <div className="mb-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white font-space mb-1">I'm a Teacher</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">Design Labs. Guide Discovery.</p>
            </div>

            <ul className="space-y-2 mb-6">
              {['Create & manage experiments', 'Monitor student progress', 'AI-graded discussions'].map((feat) => (
                <li key={feat} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400 shrink-0" />
                  {feat}
                </li>
              ))}
            </ul>

            <div className={`w-full py-3 rounded-xl font-semibold text-sm font-space text-center transition-all duration-300 flex items-center justify-center gap-2 ${
              isUpdating === 'teacher'
                ? 'bg-blue-700 text-white'
                : 'bg-blue-600 hover:bg-blue-500 text-white group-hover:shadow-lg group-hover:shadow-blue-500/20'
            }`}>
              {isUpdating === 'teacher' ? (
                <><div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> Setting up...</>
              ) : (
                <>Enter as Teacher <svg className="w-4 h-4 translate-x-0 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg></>
              )}
            </div>
          </button>

          {/* Student card */}
          <button
            onClick={() => updateRole('student')}
            disabled={isUpdating !== null}
            className="group relative p-7 rounded-2xl text-left bg-white/50 dark:bg-transparent backdrop-blur-sm transition-all duration-300 disabled:opacity-60 hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 shadow-xl dark:shadow-none"
            style={{
              background: 'linear-gradient(145deg, rgba(139,92,246,0.08) 0%, rgba(6,182,212,0.06) 100%)',
              border: '1px solid rgba(139,92,246,0.2)',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(139,92,246,0.5)';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 0 40px rgba(139,92,246,0.12)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(139,92,246,0.2)';
              (e.currentTarget as HTMLButtonElement).style.boxShadow = 'none';
            }}
          >
            {/* Icon */}
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
              style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #06b6d4 100%)' }}>
              <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1 1 .03 2.798-1.402 2.798H4.2c-1.432 0-2.402-1.798-1.402-2.798L4.2 15.3" />
              </svg>
            </div>

            <div className="mb-4">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white font-space mb-1">I'm a Student</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">Explore. Experiment. Excel.</p>
            </div>

            <ul className="space-y-2 mb-6">
              {['Join virtual lab experiments', 'Track your progress', 'Earn results & feedback'].map((feat) => (
                <li key={feat} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-violet-500 dark:bg-violet-400 shrink-0" />
                  {feat}
                </li>
              ))}
            </ul>

            <div className={`w-full py-3 rounded-xl font-semibold text-sm font-space text-center transition-all duration-300 flex items-center justify-center gap-2 ${
              isUpdating === 'student'
                ? 'text-white'
                : 'text-white group-hover:shadow-lg group-hover:shadow-violet-500/20'
            }`}
              style={{ background: isUpdating === 'student' ? '#6d28d9' : 'linear-gradient(135deg, #7c3aed 0%, #06b6d4 100%)' }}>
              {isUpdating === 'student' ? (
                <><div className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" /> Setting up...</>
              ) : (
                <>Enter as Student <svg className="w-4 h-4 translate-x-0 group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg></>
              )}
            </div>
          </button>
        </div>

      </div>
    </AuthBackground>
  );
}

// ── Protected Route ────────────────────────────────────────────────────────
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'teacher' | 'student';
}

export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  const { isLoaded, isSignedIn, user } = useUser();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) { navigate('/sign-in'); return; }
    const userRole = user?.unsafeMetadata?.role as string;
    if (!userRole) { navigate('/select-role'); return; }
    if (requiredRole && userRole !== requiredRole) {
      navigate(userRole === 'teacher' ? '/teacher/dashboard' : '/student/dashboard');
    }
  }, [isLoaded, isSignedIn, user, requiredRole, navigate]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#030712] transition-colors duration-500">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 rounded-full border-2 border-violet-500 border-t-transparent animate-spin" />
          <p className="text-slate-500 dark:text-slate-400 text-sm font-space">Loading...</p>
        </div>
      </div>
    );
  }
  if (!isSignedIn) return null;
  const userRole = user?.unsafeMetadata?.role as string;
  if (!userRole) return null;
  if (requiredRole && userRole !== requiredRole) return null;
  return <>{children}</>;
}

// ── useCurrentUser ─────────────────────────────────────────────────────────
export function useCurrentUser() {
  const { user, isLoaded, isSignedIn } = useUser();
  return {
    user: user ? {
      _id: user.id,
      id: user.id,
      email: user.primaryEmailAddress?.emailAddress || '',
      name: user.fullName || user.firstName || '',
      role: (user.unsafeMetadata?.role as 'teacher' | 'student') || null,
      imageUrl: user.imageUrl,
    } : null,
    isLoaded,
    isSignedIn,
  };
}

// ── UserProfileButton ──────────────────────────────────────────────────────
export function UserProfileButton() {
  const { user } = useUser();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const setRole = useMutation(api.users.setRole);
  const [showMenu, setShowMenu] = React.useState(false);
  const [isSwitching, setIsSwitching] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    if (showMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu]);

  const handleSignOut = async () => {
    await signOut();
    window.location.href = '/';
  };

  const currentRole = user?.unsafeMetadata?.role as 'teacher' | 'student' | undefined;
  const targetRole: 'teacher' | 'student' | null =
    currentRole === 'teacher' ? 'student'
    : currentRole === 'student' ? 'teacher'
    : null;

  const handleSwitchRole = async () => {
    if (!user || !targetRole || isSwitching) return;
    setIsSwitching(true);
    try {
      await user.update({ unsafeMetadata: { role: targetRole } });
      try {
        await setRole({
          clerkId: user.id,
          email: user.primaryEmailAddress?.emailAddress || '',
          name: user.fullName || user.firstName || '',
          role: targetRole,
        });
      } catch (convexErr) {
        console.error('Convex role sync failed (Clerk update succeeded):', convexErr);
      }
      setShowMenu(false);
      navigate(targetRole === 'teacher' ? '/teacher/dashboard' : '/student/dashboard');
    } catch (err) {
      console.error('Failed to switch role:', err);
      setIsSwitching(false);
    }
  };

  if (!user) return null;

  const RoleIcon = currentRole === 'teacher' ? Briefcase : GraduationCap;
  const roleLabel = currentRole === 'teacher' ? 'Teacher' : currentRole === 'student' ? 'Student' : null;
  const targetLabel = targetRole === 'teacher' ? 'Teacher' : targetRole === 'student' ? 'Student' : null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-2 hover:opacity-90 transition-opacity focus:outline-none"
        aria-label="User menu"
      >
        <img
          src={user.imageUrl}
          alt={user.fullName || 'User'}
          className="w-9 h-9 rounded-full ring-2 ring-white/20 dark:ring-violet-500/30 object-cover"
        />
        <span className="text-sm font-medium text-foreground hidden md:block max-w-[120px] truncate">
          {user.firstName || user.fullName || 'User'}
        </span>
      </button>

      {showMenu && (
        <div className="absolute right-0 mt-2 w-60 rounded-xl overflow-hidden z-50 shadow-xl"
          style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
          <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)' }}>
            <p className="text-sm font-semibold text-foreground truncate">{user.fullName}</p>
            <p className="text-xs text-muted-foreground truncate">{user.primaryEmailAddress?.emailAddress}</p>
            {roleLabel && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}>
                <RoleIcon className="w-3 h-3" />
                {roleLabel}
              </div>
            )}
          </div>
          {targetRole && targetLabel && (
            <button
              onClick={handleSwitchRole}
              disabled={isSwitching}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-foreground hover:bg-foreground/5 transition-colors disabled:opacity-60 disabled:cursor-not-allowed border-b"
              style={{ borderColor: 'var(--color-border)' }}
            >
              {isSwitching ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Repeat className="w-4 h-4" />
              )}
              {isSwitching ? 'Switching...' : `Switch to ${targetLabel}`}
            </button>
          )}
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-500/10 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}
